import { useEffect } from 'react'
import { Link } from '@tanstack/react-router'
import type { MemberLibraryItem } from '../api/member-access.contract'
import { MemberCatalogUnlock } from './MemberCatalogUnlock'
import {
  catalogIntro,
  lockedFrost,
  lockedVeil,
  poster,
  posterFallback,
  posterFallbackLocked,
  posterFrame,
  posterGrid,
  posterImage,
  posterImageLocked,
  posterPercent,
  posterProgress,
  posterShade,
  posterTitle,
  subtitle,
  title,
} from './member-access.css'

type CatalogItem = Pick<
  MemberLibraryItem,
  | 'productId'
  | 'name'
  | 'coverImage'
  | 'kind'
  | 'accessStatus'
  | 'progressPercent'
  | 'continueLessonId'
  | 'continueLessonTitle'
  | 'fileCount'
> & { offerUrl?: string | null }

export function MemberCatalogSection({
  producerId,
  items,
  previewProductId,
}: {
  producerId: string
  items: readonly CatalogItem[]
  previewProductId?: string
}) {
  if (items.length === 0) return null
  return (
    <section className={catalogIntro} aria-label="Catálogo">
      <h2 className={title}>Catálogo</h2>
      <p className={subtitle}>Cursos deste produtor. O que ainda não foi comprado fica bloqueado.</p>
      <MemberCatalog producerId={producerId} items={items} previewProductId={previewProductId} />
    </section>
  )
}

export function MemberCatalog({
  producerId,
  items,
  previewProductId,
  unlockingIds = [],
  onUnlockSettled,
}: {
  producerId: string
  items: readonly CatalogItem[]
  previewProductId?: string
  unlockingIds?: readonly string[]
  onUnlockSettled?: (productId: string) => void
}) {
  const unlocking = new Set(unlockingIds)
  const firstUnlock = unlockingIds[0]

  useEffect(() => {
    if (!firstUnlock || previewProductId) return
    const card = document.querySelector(`[data-testid="catalog-unlock-${firstUnlock}"]`)?.closest('a')
    if (typeof card?.scrollIntoView !== 'function') return
    card.scrollIntoView({
      block: 'center',
      inline: 'nearest',
      behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
    })
  }, [firstUnlock, previewProductId])

  return (
    <div className={posterGrid}>
      {items.map((item) => {
        const locked = item.accessStatus === 'LOCKED'
        const isUnlocking = unlocking.has(item.productId)
        const body = (
          <>
            <div className={posterFrame}>
              {item.coverImage ? (
                <img
                  className={locked ? `${posterImage} ${posterImageLocked}` : posterImage}
                  src={item.coverImage}
                  alt=""
                />
              ) : (
                <span className={locked ? `${posterFallback} ${posterFallbackLocked}` : posterFallback}>
                  {item.name}
                </span>
              )}
              <span className={posterShade} />
              {locked ? <span className={lockedFrost} /> : null}
              {locked ? <span className={lockedVeil}>Bloqueado</span> : null}
              {isUnlocking && onUnlockSettled ? (
                <MemberCatalogUnlock productId={item.productId} onSettled={onUnlockSettled} />
              ) : null}
              {!locked && item.kind === 'course' ? (
                <span className={posterPercent}>{item.progressPercent}%</span>
              ) : null}
              <span className={posterTitle}>{item.name}</span>
              {!locked && item.kind === 'course' ? (
                <span className={posterProgress} aria-hidden>
                  <span style={{ width: `${item.progressPercent}%` }} />
                </span>
              ) : null}
            </div>
          </>
        )
        if (locked && previewProductId) {
          return (
            <Link
              key={item.productId}
              className={poster}
              to="/area-membros/preview/$productId/oferta"
              params={{ productId: item.productId }}
              data-testid={`catalog-locked-${item.productId}`}
            >
              {body}
            </Link>
          )
        }
        if (locked) {
          return (
            <Link
              key={item.productId}
              className={poster}
              to="/acesso/$producerId/cursos/$productId/oferta"
              params={{ producerId, productId: item.productId }}
              data-testid={`catalog-locked-${item.productId}`}
            >
              {body}
            </Link>
          )
        }
        if (previewProductId) {
          if (item.continueLessonId) {
            return (
              <Link
                key={item.productId}
                className={poster}
                to="/area-membros/preview/$productId/aulas/$lessonId"
                params={{ productId: item.productId, lessonId: item.continueLessonId }}
              >
                {body}
              </Link>
            )
          }
          return (
            <Link
              key={item.productId}
              className={poster}
              to="/area-membros/preview/$productId/curso"
              params={{ productId: item.productId }}
            >
              {body}
            </Link>
          )
        }
        if (item.kind === 'files') {
          return (
            <Link
              key={item.productId}
              className={poster}
              to="/acesso/$producerId/arquivos/$productId"
              params={{ producerId, productId: item.productId }}
              data-unlocking={isUnlocking ? 'true' : undefined}
            >
              {body}
            </Link>
          )
        }
        if (item.continueLessonId) {
          return (
            <Link
              key={item.productId}
              className={poster}
              to="/acesso/$producerId/cursos/$productId/aulas/$lessonId"
              params={{ producerId, productId: item.productId, lessonId: item.continueLessonId }}
              data-unlocking={isUnlocking ? 'true' : undefined}
            >
              {body}
            </Link>
          )
        }
        return (
          <Link
            key={item.productId}
            className={poster}
            to="/acesso/$producerId/cursos/$productId"
            params={{ producerId, productId: item.productId }}
            data-unlocking={isUnlocking ? 'true' : undefined}
          >
            {body}
          </Link>
        )
      })}
    </div>
  )
}
